import {
  IonButton,
  IonButtons,
  IonHeader,
  IonIcon,
  IonModal,
  IonTitle,
  IonToolbar,
  isPlatform,
} from '@ionic/react';
import { close } from 'ionicons/icons';
import { cloneDeep } from 'lodash';
import {
  ReactElement,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  ScratchpadBucket,
  ScratchpadBucketScope,
  ScratchpadBucketType,
  useListScratchpadBucketsQuery,
} from '../../../../../api/graphql/generated';
import { useScratchpadServiceContext } from '../../../../../components/machine/scratchpad/scratchpad-service-context-provider';
import { getLocalizedText } from '../../../../../i18n/i18n.utils';
import { useIsMachineActive } from '../../../../../pages/machine/machine-context-provider';
import { useFlowServices } from '../../hook/flow-service.hooks';
import { ApplicationDefaultParameterSet, Flow } from '../../model/flow.types';
import { ApplicationSettingsEdit } from './application-settings-edit';
import { ApplicationSettingsSearch } from './application-settings-search';

export interface ApplicationSettingsModalRef {
  showApplicationSettings: () => Promise<boolean>;
}

export interface ApplicationSettingsModalProviderProps {
  isOpen?: boolean;
  readOnly?: boolean;
  onDismiss?: () => void;
  presentingEl?: HTMLElement;
  defaultBucketTags?: Record<string, unknown>;
  type: ScratchpadBucketType;
}

const ApplicationSettingsModalProvider = forwardRef<
  ApplicationSettingsModalRef,
  ApplicationSettingsModalProviderProps
>((props: ApplicationSettingsModalProviderProps, ref): ReactElement => {
  const {
    defaultBucketTags,
    readOnly,
    presentingEl,
    onDismiss,
    isOpen: externalIsOpen,
    type,
  } = props;

  const closedBucketExplorer = useRef<boolean>(false);
  const isMachineActive = useIsMachineActive();
  const { appSlug, flow, flowService, system } = useFlowServices();
  const cbRef = useRef<() => void>();
  const [isOpen, setOpen] = useState<boolean>(false);
  const resolveRef = useRef<(value: boolean | PromiseLike<boolean>) => void>();
  const [showBucketExplorer, setShowBucketExplorer] = useState(false);
  const { scratchpad, save } = useScratchpadServiceContext();

  const { data: listBucketsData, loading: listBucketsDataLoading } =
    useListScratchpadBucketsQuery({
      variables: {
        input: {
          scope: ScratchpadBucketScope.MACHINE,
          parentId: scratchpad.machineId,
          scratchpadAppSlug: appSlug,
          active: true,
          tags: defaultBucketTags,
          type,
        },
      },
      fetchPolicy: 'cache-first', // no need for a network request just to check if we should present the explorer
    });

  const [showExplorerFooter, setShowExplorerFooter] = useState(false);

  useEffect(() => {
    if (externalIsOpen) {
      setOpen(externalIsOpen);
    }
  }, [externalIsOpen]);

  useEffect(() => {
    if (isOpen && isMachineActive) {
      if (
        closedBucketExplorer.current !== true &&
        !flow.items?.length &&
        !flow.defaultParameterSet?.data?.parameters &&
        // We want to display the explorer if the list is loading.
        (listBucketsData?.listScratchpadBuckets.total || listBucketsDataLoading)
      ) {
        setShowExplorerFooter(true);
        setShowBucketExplorer(true);
      }
    }
  }, [
    flow,
    listBucketsData,
    isOpen,
    listBucketsData?.listScratchpadBuckets.total,
    isMachineActive,
    listBucketsDataLoading,
  ]);

  const showApplicationSettings = (): Promise<boolean> => {
    return new Promise((resolve) => {
      setOpen(true);
      resolveRef.current = resolve;
    });
  };

  useImperativeHandle(ref, () => {
    return {
      showApplicationSettings,
    };
  });

  const onClose = useCallback((cb?: () => void) => {
    setOpen(false);
    cbRef.current = cb;
  }, []);

  const didDismiss = async () => {
    if (showBucketExplorer) {
      setShowBucketExplorer(false);
    }

    if (isOpen) {
      setOpen(false);
    }

    if (resolveRef.current) {
      const valid = await flowService.isFlowValidForTeaching(flow);
      resolveRef.current(valid);
    }

    if (typeof onDismiss === 'function') {
      onDismiss();
    }
    closedBucketExplorer.current = false;
  };

  const shouldUseBackdropDismiss = !isPlatform('capacitor');
  const settingsModalRef = useRef<HTMLIonModalElement>(null);

  const getBucketSubheading = useCallback(
    (bucket: ScratchpadBucket) => {
      return flowService.getApplicationDefaultParameterSetSubheadingFromBucket(
        bucket
      );
    },
    [flowService]
  );

  return (
    <>
      <IonModal
        ref={settingsModalRef}
        isOpen={isOpen}
        canDismiss={true}
        presentingElement={presentingEl ?? undefined}
        onDidDismiss={() => didDismiss()}
        backdropDismiss={shouldUseBackdropDismiss}
      >
        <IonHeader>
          <IonToolbar>
            <IonButtons slot={'start'}>
              <IonButton onClick={() => onClose()}>
                <IonIcon icon={close} size="large" />
              </IonButton>
            </IonButtons>
            <IonTitle>{getLocalizedText('common:settings')}</IonTitle>
          </IonToolbar>
        </IonHeader>
        <ApplicationSettingsEdit
          afterSave={() => {
            onClose();
          }}
          readOnly={readOnly || !isMachineActive}
          showBucketExplorer={setShowBucketExplorer}
          presentingElement={
            settingsModalRef.current ?? presentingEl ?? undefined
          }
        />
      </IonModal>
      <ApplicationSettingsSearch
        isOpen={showBucketExplorer}
        presentingElement={settingsModalRef.current ?? presentingEl}
        onClose={() => {
          closedBucketExplorer.current = true;
          setShowBucketExplorer(false);
          setShowExplorerFooter(false);
        }}
        onSelected={async (bucket) => {
          const defaultParameterSet: ApplicationDefaultParameterSet = {
            id: bucket.id,
            version: String(bucket.version),
            data: cloneDeep(bucket.data),
          };

          let flow: Flow = {
            ...scratchpad.appData,
            defaultParameterSet,
            // Handle backwards compatibility
            weldParameterSet: defaultParameterSet,
          };

          flow = flowService.mapApplicationDefaultParameterSetTagsToFlowConfig(
            flow,
            bucket.tags
          );

          await save(
            { ...scratchpad, bucket1Id: bucket.id, appData: flow },
            {
              saveAsDelta: true,
            }
          );
          setShowBucketExplorer(false);
          onClose();
        }}
        system={system}
        showFooter={showExplorerFooter}
        implicitSearchTags={defaultBucketTags ?? {}}
        getBucketSubheading={getBucketSubheading}
        type={type}
      />
    </>
  );
});

ApplicationSettingsModalProvider.displayName =
  'ApplicationSettingsModalProvider';

export { ApplicationSettingsModalProvider };
